import RestUtilities from "lib/RestUtilities";
import { RestUtilWithCancel } from "lib/RestUtilWithCancel";
import { JobLogEventType, MemberType, NotificationEmailOption, NotificationType, PublicationType, SystemRoles } from "models/ApiEnums";
import {
    AboutInfoDto, AwardHistoryDto,
    AwardHistoryGraphDto, AwardRankingListResult, AzureSearchImageResult,
    BadgeAdminListDto, BlockCodeDto,
    CourseRecordFormConfigDto, DashboardAwardAnalysisParams,
    DashboardConsumptionAnalysisParams, DashboardCourseAdminAnalysisModel,
    DashboardCourseAnalysisParams, DashboardKnowledgeTestAnalysisParams, DashboardKnowledgeTestAppliedParams,
    DashboardKnowledgeTestIndicatorsParams, DashboardKnowledgeIndicatorsResumeModel, DashboardPublicationAnalysisParams, DashboardUserAndGroupsParticipationAccessParams,
    DashboardUserAndGroupsParticipationKnowledgeTestParams, DashboardUserAndGroupsParticipationPublicationParams,
    DashboardUserEvolutionKnowdledgeParams, DashboardUserEvolutionParticiationListModel,
    DashboardUserEvolutionParticipationParams, DashboardUserEvolutionParticipationResumeModel, DashboardUserEvolutionPublicationListModel,
    DashboardUserEvolutionPublicationParams, DashboardUserEvolutionPublicationResumeModel, FeedItemBodyDto,
    GenericListItemDto,
    GetAgreementDto, GetAwardDto,
    GetAwardTopicExceptionDto, GetBadgeDto,
    GetBadgeListParams,
    GetCourseAdminListParams, GetGroupDto, GetGroupListParams,
    GetKnowledgeTestAdminListParams,
    GetKnowledgeTestTopicsParams, GetKwownledgeTestDto,
    GetMediaFileListParams, GetPublicationAdminListParams, GetSetupAdminDto,
    GetSetupAgreementDto,
    GetSetupIntegrationDto, GetSetupKnowledgeTestDto, GetSetupPublicationDto, GetSetupUserDto, GetTopicDto, GetTopicListAdminParams,
    GetTopicListParams, GetUserAccountDto, GetUserDto, GetUserListParams, GetUserProfileDto,
    GlobalDocumentSearchFilter, GlobalPublicationSearchFilter,
    GlobalSearchDocumentOrderBy, GlobalSearchPublicationOrderBy,
    GlobalSearchRecord, GlobalSearchResult,
    GroupAdminListDto, GroupShortView,
    GroupSuggestionDto, ImageSearchFilter,
    ImportUsersResultDto, KnowledgeTestAdminItemDto,
    KnowledgeTestAnswersDistributionModel,
    KnowledgeTestResultAnalysisModel,
    KnowledgeTestTopicItemDto, KnowledgeTestTopicSuggestionDto,
    KnowledgeTestWhoGotRightDto, LocationDto, LogRecord,
    MediaServiceFileDto, MemberShortViewModel, MemberSuggestionDto,
    NotificationCalloutDto, NotificationDto,
    NotificationSetupDto, NotificationSetupItemDto, PagedResult,
    PageRange,
    PublicationAdminCourseDto, PublicationAdminItemDto,
    PublicationShortView,
    PublicationSuggestionDto,
    QrCodeDto,
    QueueEventViewModel,
    QueueViewModelParams, QuizSaveResultDto, QuizSessionDto,
    QuizSessionSaveAnswerDto, QuizTopicDiscoveryModel,
    ReleaseNoteDto, SaveAccountDto,
    SaveAwardDto,
    SaveAwardTopicExceptionDto, SaveBadgeDto, SaveGroupDto,
    SaveKnowledgeTestDto, SavePassportResult, SaveSetupAdminDto,
    SaveSetupAgreementDto,
    SaveSetupIntegrationDto, SaveSetupKnowledgeTestDto, SaveSetupPublicationDto, SaveSetupUserDto, SaveTopicDto, SaveUserDto,
    TimezoneRecordDto,
    TopicDashboardKnowledgeDatabaseParams,
    TopicDashboardResultsParams, TopicGraphDto, TopicListAdminItemDto,
    TopicRecordFormConfigDto,
    TopicRelationshipDto, TopicShortViewModel, TopicSpecialistDto, TopicSuggestionDto,
    TopicTreeItemDto,
    TopicUserFlowListItemDto, UserAdminListDto,
    UserBadgeDto, UserCredentialsModel, UserFollowingAssetLookup,
    UserKnowsAboutGetDto,
    UserKnowsAboutPostDto,
    UserProfileRelationDto, UserRecordFormConfigDto,
    UserShortView,
    UserSidebarFollowingAssetLookup, UserTopicInterationDto, DashboardConsumptionResultModel, JobLogParams, JobLogDto, JobLogEventTypeDto
} from "models/ApiModels";

//#region GLOBAL

export function getCultures(languageCode?: string): Promise<KeyValueItem<string, string>[]> {
    return RestUtilities.get<KeyValueItem<string, string>[]>("/api/v1.0/System/LookupCultures", { languageCode: languageCode || null });
}

export function getTimezones(culture: string): Promise<TimezoneRecordDto[]> {
    return RestUtilities.get<TimezoneRecordDto[]>("/api/v1.0/System/AllTimezones", { culture });
}

export function getAntiForgery(): Promise<void> {
    return RestUtilities.get("/api/antiforgery");
}

export function getAboutInfo(): Promise<AboutInfoDto> {
    return RestUtilities.get("/api/v1.0/System/AboutInfo");
}

export function getReleaseNotes(): Promise<ReleaseNoteDto[]> {
    return RestUtilities.get("/api/v1.0/System/GetReleaseNotes");
}

export function getReleaseNote(releaseNumber: string): Promise<string> {
    return RestUtilities.get("/api/v1.0/System/GetReleaseNote", { releaseNumber });
}

//#endregion

//#region USER

export function getLicensesRemaining(): Promise<number> {
    return RestUtilities.get("/api/v1.0/User/LicensesRemaining", {});
}

export function acceptCookies(): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/Session/AcceptCookies", {});
}

export function getUserEndorseList(userId: string, topicId: string, searchText: string, offset: number, take: number): Promise<PagedResult<GenericListItemDto>> {
    return RestUtilities.get<PagedResult<GenericListItemDto>>("/api/v1.0/User/GetEndorseList", { userId, topicId, searchText, offset, take });
}

export function getUserFormEditOptions(culture: string): Promise<UserRecordFormConfigDto> {
    return RestUtilities.get("/api/v1.0/User/GetRecordFormConfig", { culture });
}

export function getUserProfile(userId: string): Promise<GetUserProfileDto> {
    return RestUtilities.get<GetUserProfileDto>("/api/v1.0/User/GetUserProfile", { userId });
}

export function getAccount(): Promise<GetUserAccountDto> {
    return RestUtilities.get("/api/v1.0/Account/GetAccount", {});
}

export function updateAccount(dto: SaveAccountDto): Promise<GetUserAccountDto> {
    return RestUtilities.put("/api/v1.0/Account/UpdateAccount", dto);
}

export function changeAccountPassword(userId: string, oldPassword: string, newPassword: string): Promise<void> {
    return RestUtilities.post("/api/v1.0/Account/ChangePassword", { userId, oldPassword, newPassword });
}

export function importUsers(csv: string, isPublisher: boolean, isPublisherVideo: boolean): Promise<ImportUsersResultDto> {
    return RestUtilities.post("/api/v1.0/User/ImportUsers", { csv, isPublisher, isPublisherVideo });
}

export function getUserRecord(userId: string): Promise<GetUserDto> {
    return RestUtilities.get("/api/v1.0/User/GetRecord", { userId });
}

export function saveUserRecord(dto: SaveUserDto): Promise<SavePassportResult> {
    return RestUtilities.post("/api/v1.0/User/Save", dto);
}

export function deleteUser(userId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/User/Delete", { userId });
}

export function recoverUser(userId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/User/Recover", { userId });
}

export function getUserLog(userId: string, offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/User/GetLog", { userId, offset, take });
}

export function getUserFollowingTopics(): Promise<UserFollowingAssetLookup[]> {
    return RestUtilities.get<UserFollowingAssetLookup[]>("/api/v1.0/User/GetUserFollowingTopics", {});
}

export function getUserSidebarFollowingTopics(): Promise<UserSidebarFollowingAssetLookup[]> {
    return RestUtilities.get<UserSidebarFollowingAssetLookup[]>("/api/v1.0/User/GetSidebarFollowingTopics", {});
}

export function getUserFollowingPublications(): Promise<UserFollowingAssetLookup[]> {
    return RestUtilities.get<UserFollowingAssetLookup[]>("/api/v1.0/User/GetUserFollowingPublications", {});
}

export function getAdminUserList(p: GetUserListParams): Promise<PagedResult<UserAdminListDto>> {
    return RestUtilities.post("/api/v1.0/User/GetAdminList", p);
}

export function getManagerSuggestions() {
    return new RestUtilWithCancel<AutoCompleteDataModel[], { searchText: string, refUserId: string, offset: number, take: number; }>("post", "/api/v1.0/User/GetManagerSuggestions");
}

export function getAuthorSuggestions() {
    return new RestUtilWithCancel<AutoCompleteDataModel[], { searchText: string, refUserId: string, offset: number, take: number; }>("post", "/api/v1.0/User/GetAuthorSuggestions");
}

export function getTeamSuggestions() {
    return new RestUtilWithCancel<AutoCompleteDataModel[], { managerId: string, deepSearch: boolean, searchText: string, offset: number, take: number; }>("post", "/api/v1.0/User/GetTeamSuggestions");
}

export function getUserSuggestions() {
    return new RestUtilWithCancel<AutoCompleteDataModel[], { searchText: string, refUserId: string, offset: number, take: number; }>("post", "/api/v1.0/User/GetUserSuggestions");
}

export function getUserGroups(userId: string, expanded: boolean): Promise<GroupShortView[]> {
    return RestUtilities.get("/api/v1.0/User/GetUserGroups", { userId, expanded });
}

export function getUserCredentials(userId: string, offset: number, take: number): Promise<PagedResult<UserCredentialsModel>> {
    return RestUtilities.get("/api/v1.0/User/GetUserCredentials", { userId, offset, take });
}

export function getUserKnowsAbout(userId: string, offset: number, take: number): Promise<PagedResult<UserKnowsAboutGetDto>> {
    return RestUtilities.get("/api/v1.0/User/GetUserKnowsAbout", { userId, offset, take });
}

export function updateUserKnowsAbout(userId: string, KnowsAbout: UserKnowsAboutPostDto): Promise<PagedResult<UserKnowsAboutGetDto>> {
    return RestUtilities.post("/api/v1.0/User/UpdateKnowsAbout", { userId, KnowsAbout });
}

export function checkUserNameIsUnique(userId: string, name: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/User/CheckNameIsUnique", { userId, name });
}

export function checkUserEmailIsUnique(userId: string, email: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/User/CheckEmailIsUnique", { userId, email });
}

export function getQrCodeToken(): Promise<QrCodeDto> {
    return RestUtilities.get("/api/v1.0/User/GetQrCodeToken", {});
}

export function userMuteTopic(topicId: string): Promise<UserTopicInterationDto> {
    return RestUtilities.post("/api/v1.0/User/MuteTopic", { topicId });
}

export function userUnmuteTopic(topicId: string): Promise<UserTopicInterationDto> {
    return RestUtilities.post("/api/v1.0/User/UnmuteTopic", { topicId });
}

export function userMuteAuthor(authorId: string): Promise<UserProfileRelationDto> {
    return RestUtilities.post("/api/v1.0/User/MutePerson", { authorId });
}

export function userUnmuteAuthor(authorId: string): Promise<UserProfileRelationDto> {
    return RestUtilities.post("/api/v1.0/User/UnmutePerson", { authorId });
}

export function userBlockAnotherUser(anotherUserId: string): Promise<UserProfileRelationDto> {
    return RestUtilities.post("/api/v1.0/User/BlockPerson", { anotherUserId });
}

export function userUnblockAnotherUser(anotherUserId: string): Promise<UserProfileRelationDto> {
    return RestUtilities.post("/api/v1.0/User/UnblockPerson", { anotherUserId });
}

export function getUserWonBadge(userId: string, badgeId: string): Promise<UserBadgeDto> {
    return RestUtilities.get("/api/v1.0/User/GetWonBadge", { userId, badgeId });
}

export function getUserAllBadges(userId: string): Promise<UserBadgeDto[]> {
    return RestUtilities.get("/api/v1.0/User/GetAllBadges", { userId });
}

export function followUser(followedUserId: string, following: boolean): Promise<UserProfileRelationDto> {
    return RestUtilities.post("/api/v1.0/User/FollowUser", { followedUserId, following });
}

export function setFollowedUserNotification(followedUserId: string, enabled: boolean): Promise<UserProfileRelationDto> {
    return RestUtilities.post("/api/v1.0/User/SetFollowedNotification", { followedUserId, enabled });
}

export function followTopic(topicId: string, status: boolean): Promise<UserTopicInterationDto> {
    return RestUtilities.post("/api/v1.0/User/FollowTopic", { topicId, status });
}

export function setFollowedTopicNotification(topicId: string, enabled: boolean): Promise<UserTopicInterationDto> {
    return RestUtilities.post("/api/v1.0/User/SetTopicNotification", { topicId, enabled });
}

export function getUserSimulatedGroups(groups: string[]): Promise<GroupShortView[]> {
    return RestUtilities.post("/api/v1.0/User/GetSimulatedGroups", { groups });
}

export function getUserSimulatedGroupsWithPermission(groups: string[], roles: SystemRoles): Promise<GroupShortView[]> {
    return RestUtilities.post("/api/v1.0/User/GetSimulatedGroupsWithPermission", { groups, roles });
}

export function getUserFollowers(userId: string, searchText: string, range: PageRange): Promise<PagedResult<UserShortView>> {
    return RestUtilities.post("/api/v1.0/user/Followers", { userId, searchText, range });
}

export function getManagerTree(rootUserId?: string): Promise<string> {
    return RestUtilities.get("/api/v1.0/User/GetManagerTree", rootUserId == null ? {} : { rootUserId });
}

//#endregion

//#region GROUP

export function checkGroupNameIsUnique(groupId: string, name: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/Group/CheckNameIsUnique", { groupId, name });
}

export function checkCanAddMemberIntoGroup(groupId: string, memberId: string): Promise<string> {
    return RestUtilities.get("/api/v1.0/Group/CanAddMember", { groupId, memberId });
}

export function checkCanAddTopicIntoGroup(groupId: string, topicId: string): Promise<string> {
    return RestUtilities.get("/api/v1.0/Group/CanAddTopic", { groupId, topicId });
}

export function joinGroups(sourceGroupId: string, destinationGroupId: string): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/Group/JoinGroups", { sourceGroupId, destinationGroupId });
}

export function getGroupSuggestions() {
    return new RestUtilWithCancel<GroupSuggestionDto[], { searchText: string, offset: number, take: number; }>("get", "/api/v1.0/Group/GetSuggestions");
}

export function getAdminGroupList(p: GetGroupListParams): Promise<PagedResult<GroupAdminListDto>> {
    return RestUtilities.post("/api/v1.0/Group/GetAdminList", p);
}

export function getGroupRecord(groupId: string): Promise<GetGroupDto> {
    return RestUtilities.get("/api/v1.0/Group/GetRecord", { groupId });
}

export function getGroupLog(groupId: string, offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/Group/GetLog", { groupId, offset, take });
}

export function saveGroupRecord(dto: SaveGroupDto): Promise<void> {
    return RestUtilities.post("/api/v1.0/Group/Save", dto);
}

export function deleteGroup(groupId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/Group/Delete", { groupId });
}

export function getGroupSimulatedSuggestedTopics(groupId: string, newTopics: string[], delTopics: string[], searchText: string, range: PageRange): Promise<PagedResult<TopicShortViewModel>> {
    return RestUtilities.post("/api/v1.0/Group/GetSimulatedSuggestedTopics", { groupId, newTopics, delTopics, searchText, range });
}


//#endregion

//#region MEMBER

export function getSimulatedGroupUsers(groupId: string, newMembers: string[], delMembers: string[], searchText: string, range: PageRange): Promise<PagedResult<MemberShortViewModel>> {
    return RestUtilities.post("/api/v1.0/Member/GetSimulatedGroupUsers", { groupId, newMembers, delMembers, searchText, range });
}

export function getSimulatedGroupMembers(groupId: string, newMembers: string[], delMembers: string[], searchText: string, range: PageRange): Promise<PagedResult<MemberShortViewModel>> {
    return RestUtilities.post("/api/v1.0/Member/GetSimulatedGroupMembers", { groupId, newMembers, delMembers, searchText, range });
}

export function getMemberSuggestions() {
    return new RestUtilWithCancel<MemberSuggestionDto[], { searchText: string, usersOnly: boolean, excludeCurrentUser: boolean, offset: number, take: number; }>("post", "/api/v1.0/Member/GetMemberSuggestions");
}

export function getPublicationMemberSuggestions() {
    return new RestUtilWithCancel<MemberSuggestionDto[], { publicationId: string, searchText: string, usersOnly: boolean, excludeCurrentUser: boolean, offset: number, take: number; }>("post", "/api/v1.0/Member/GetPublicationMemberSuggestions");
}

export function getTopicMemberSuggestions() {
    return new RestUtilWithCancel<MemberSuggestionDto[], { topicId: string, searchText: string, usersOnly: boolean, excludeCurrentUser: boolean, offset: number, take: number; }>("post", "/api/v1.0/Member/GetTopicMemberSuggestions");
}

export function getTopicSubordinateMemberSuggestions() {
    return new RestUtilWithCancel<MemberSuggestionDto[], { topicId: string, searchText: string, offset: number, take: number; }>("post", "/api/v1.0/Member/GetTopicSubordinateMemberSuggestions");
}


export function getGroupSelection(searchText: string, offset: number, take: number): Promise<PagedResult<GenericListItemDto>> {
    return RestUtilities.get<PagedResult<GenericListItemDto>>("/api/v1.0/Member/GetMemberSelection", { searchText, memberType: MemberType.Group, offset, take });
}

export function getUserSelection(searchText: string, offset: number, take: number): Promise<PagedResult<GenericListItemDto>> {
    return RestUtilities.get<PagedResult<GenericListItemDto>>("/api/v1.0/Member/GetMemberSelection", { searchText, memberType: MemberType.User, offset, take });
}


export function getSimulatedTopicUsers(topicId: string, newMembers: string[], delMembers: string[], searchText: string, range: PageRange): Promise<PagedResult<MemberShortViewModel>> {
    return RestUtilities.post("/api/v1.0/Member/GetSimulatedTopicUsers", { topicId, newMembers, delMembers, searchText, range });
}

export function getSimulatedTopicMembers(topicId: string, newMembers: string[], delMembers: string[], searchText: string, range: PageRange): Promise<PagedResult<MemberShortViewModel>> {
    return RestUtilities.post("/api/v1.0/Member/GetSimulatedTopicMembers", { topicId, newMembers, delMembers, searchText, range });
}

export function getSimulatedTopicAdministrators(topicId: string, newMembers: string[], delMembers: string[], searchText: string, range: PageRange): Promise<PagedResult<MemberShortViewModel>> {
    return RestUtilities.post("/api/v1.0/Member/GetSimulatedTopicAdministrators", { topicId, newMembers, delMembers, searchText, range });
}

//#endregion

//#region TOPIC

export function checkCanAddMemberIntoTopic(topicId: string, memberId: string): Promise<string> {
    return RestUtilities.get("/api/v1.0/Topic/CanAddMember", { topicId, memberId });
}

export function getTopicFormEditOptions(): Promise<TopicRecordFormConfigDto> {
    return RestUtilities.get("/api/v1.0/Topic/GetRecordFormConfig", {});
}

export function getTopicSelection(searchText: string, offset: number, take: number): Promise<PagedResult<GenericListItemDto>> {
    return RestUtilities.get<PagedResult<GenericListItemDto>>("/api/v1.0/Topic/GetTopicSelection", { searchText, offset, take });
}

export function getTopicSuggestions() {
    return new RestUtilWithCancel<TopicSuggestionDto[], { userId: string, searchText: string, offset: number, take: number, skipTopicAccessCheckFor: SystemRoles; }>("get", "/api/v1.0/Topic/GetSuggestions");
}

export function getTopicListToUser(p: GetTopicListParams): Promise<PagedResult<TopicUserFlowListItemDto>> {
    return RestUtilities.post("/api/v1.0/Topic/GetTopicListToUser", p);
}

export function getTopicAdminList(p: GetTopicListAdminParams): Promise<PagedResult<TopicListAdminItemDto>> {
    return RestUtilities.post("/api/v1.0/Topic/GetAdminList", p);
}

export function getTopic(topicId: string): Promise<GetTopicDto> {
    return RestUtilities.post<GetTopicDto>("/api/v1.0/Topic/GetRecord", { topicId });
}

export function saveTopic(dto: SaveTopicDto): Promise<string> {
    return RestUtilities.post("/api/v1.0/Topic/Save", dto);
}

export function deleteTopic(topicId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/Topic/Delete", { topicId });
}

export function joinTopics(sourceTopicId: string, destinationTopicId: string): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/Topic/JoinTopics", { sourceTopicId, destinationTopicId });
}

export function getTopicLog(topicId: string, offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.post("/api/v1.0/Topic/GetLog", { topicId, offset, take });
}

export function getTopicSpecialists(topicId: string, searchText: string, offset: number, take: number): Promise<PagedResult<TopicSpecialistDto>> {
    return RestUtilities.get("/api/v1.0/Topic/GetSpecialists", { topicId, searchText, offset, take });
}

export function getTopicDashboardTrustAnalysis(topicId: string): Promise<KnowledgeTestResultAnalysisModel> {
    return RestUtilities.post("/api/v1.0/Topic/GetDashboardTrustAnalysis", { topicId });
}

export function getTopicDashboardKnowledgeDatabase(p: TopicDashboardKnowledgeDatabaseParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Topic/GetDashboardKnowledgeDatabase", p);
}

export function getTopicDashboardResults(p: TopicDashboardResultsParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Topic/GetDashboardResults", p);
}

export function checkTopicMemberIsRequiredInKnowledgeTest(topicId: string, memberId: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/Topic/IsRequiredMemberInKnowledgeTest", { topicId, memberId });
}

export function checkTopicMember(topicId: string, memberId: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/Topic/IsMember", { topicId, memberId });
}

export function getTopicRequiredMembers(topicId: string, searchText: string, offset: number, take: number): Promise<PagedResult<MemberSuggestionDto>> {
    return RestUtilities.get("/api/v1.0/Topic/GetRequiredMembers", { topicId, searchText, offset, take });
}

export function getTopicRequiredMembersSuggestions() {
    return new RestUtilWithCancel<MemberSuggestionDto[], { topicId: string, searchText: string, offset: number, take: number; }>("get", "/api/v1.0/Topic/GetRequiredMembersSuggestions");
}

export function topicAddRequiredMember(topicId: string, memberId: string): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/Topic/AddRequiredMember", { topicId, memberId });
}

export function topicDeleteRequiredMember(topicId: string, memberId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/Topic/DeleteRequiredMember", { topicId, memberId });
}

export function topicEndorsementUpdate(topicId: string, userId: string, endorsed: boolean): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/Topic/UpdateTopicEndorsement", { topicId, userId, endorsed }, true);
}

export function addManualTopicRelationship(topicId: string, relatedTopicId: string): Promise<TopicRelationshipDto> {
    return RestUtilities.post("/api/v1.0/Topic/AddManualTopicRelationship", { topicId, relatedTopicId });
}

export function deleteManualTopicRelationship(topicId: string, relatedTopicId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/Topic/DeleteManualTopicRelationship", { topicId, relatedTopicId });
}

export function getTopicRelationships(topicId: string): Promise<TopicRelationshipDto[]> {
    return RestUtilities.get("/api/v1.0/Topic/GetTopicRelationships", { topicId });
}

export function getTopicDiagram(rootTopicId?: string): Promise<TopicGraphDto> {
    return RestUtilities.get("/api/v1.0/Topic/GetDiagram", rootTopicId == null ? {} : { rootTopicId });
}

export function getTopicTree(rootTopicId?: string): Promise<TopicTreeItemDto[]> {
    return RestUtilities.get("/api/v1.0/Topic/GetTree", rootTopicId == null ? {} : { rootTopicId });
}

//#endregion

//#region PUBLICATION

export function getPublicationShortView(publicationId: string): Promise<PublicationShortView> {
    return RestUtilities.get("/api/v1.0/Publication/GetPublicationShortView", { publicationId });
}

export function getPublicationAdminList(p: GetPublicationAdminListParams): Promise<PagedResult<PublicationAdminItemDto>> {
    return RestUtilities.post("/api/v1.0/Publication/GetAdminList", p);
}

export function getPublicationAdminBody(publicationId: string): Promise<FeedItemBodyDto> {
    return RestUtilities.get("/api/v1.0/Publication/GetAdminPublicationBody", { publicationId });
}

export function getPublicationLog(publicationId: string, offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/Publication/GetLog", { publicationId, offset, take });
}

export function checkPublicationTitleIsUnique(userId: string, publicationType: PublicationType, currentPublicationId: string, title: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/Publication/CheckTitleIsUnique", { userId, publicationType, currentPublicationId, title });
}

export function getPublicationSuggestions() {
    return new RestUtilWithCancel<PublicationSuggestionDto[], { userId: string, searchText: string, publicationTypes: PublicationType[], offset: number, take: number; }>("get", "/api/v1.0/Publication/GetSuggestions");
}

//#endregion

//#region COURSE

export function getCourseRecordFormConfig(): Promise<CourseRecordFormConfigDto> {
    return RestUtilities.get("/api/v1.0/Publication/GetCourseRecordFormConfig", {});
}
export function getCoursePublicationAdminList(p: GetCourseAdminListParams): Promise<PagedResult<PublicationAdminCourseDto>> {
    return RestUtilities.post("/api/v1.0/Publication/GetCoursePublicationAdminList", p);
}

export function getCoursePublicationAdmin(courseId: string): Promise<PublicationAdminCourseDto> {
    return RestUtilities.get("/api/v1.0/Publication/GetCoursePublicationAdmin", { courseId });
}

export function getCourseSuggestions() {
    return new RestUtilWithCancel<PublicationSuggestionDto[], { userId: string, searchText: string, offset: number, take: number; }>("get", "/api/v1.0/Publication/GetCourseSuggestions");
}

export function getCourseEnrollMembersSuggestions() {
    return new RestUtilWithCancel<MemberSuggestionDto[], { courseId: string, searchText: string, offset: number, take: number; }>("get", "/api/v1.0/Publication/GetCourseEnrollMembersSuggestions");
}


//#endregion

//#region QUIZ

export function quizDiscoveryTopics(userId: string): Promise<QuizTopicDiscoveryModel[]> {
    return RestUtilities.get("/api/v1.0/Quiz/DiscoveryTopics", { userId });
}

export function quizCreateSession(userId: string, p: QuizTopicDiscoveryModel): Promise<string> {
    return RestUtilities.post("/api/v1.0/Quiz/CreateSession", {
        userId,
        topicId: p.topicId,
        sequential: p.sequential,
        revision: p.revision,
        maxItems: p.computedTestsToAnswer,
        maxPoints: p.computedAwardPoints
    });
}

export function quizCreateTopicSession(userId: string, topicId: string): Promise<string> {
    return RestUtilities.post("/api/v1.0/Quiz/CreateTopicSession", { userId, topicId });
}

export function quizGetBreakSession(userId: string): Promise<QuizSessionDto> {
    return RestUtilities.get("/api/v1.0/Quiz/GetBreakSession", { userId });
}

export function quizGetSession(sessionId: string): Promise<QuizSessionDto> {
    return RestUtilities.get("/api/v1.0/Quiz/GetSession", { sessionId });
}

export function quizCreateNextDynamicItems(sessionId: string): Promise<QuizSessionDto> {
    return RestUtilities.post("/api/v1.0/Quiz/CreateNextDynamicItems", { sessionId });
}

export function quizSaveAnswer(p: QuizSessionSaveAnswerDto): Promise<QuizSaveResultDto> {
    return RestUtilities.post("/api/v1.0/Quiz/SaveAnswer", p);
}

//#endregion

//#region KNOWLEDGE TEST

export function getKnowledgeTestTopics(p: GetKnowledgeTestTopicsParams): Promise<PagedResult<KnowledgeTestTopicItemDto>> {
    return RestUtilities.post("/api/v1.0/KnowledgeTest/GetTopics", p);
}

export function getKnowledgeTestAdminList(p: GetKnowledgeTestAdminListParams): Promise<PagedResult<KnowledgeTestAdminItemDto>> {
    return RestUtilities.post("/api/v1.0/KnowledgeTest/GetAdminList", p);
}

export function getKnowledgeTest(testId: string): Promise<GetKwownledgeTestDto> {
    return RestUtilities.get("/api/v1.0/KnowledgeTest/GetTest", { testId });
}

export function saveKnowledgeTest(dto: SaveKnowledgeTestDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/KnowledgeTest/SaveTest", dto);
}

export function deleteKnowledgeTest(testId: string): Promise<boolean> {
    return RestUtilities.delete<boolean>("/api/v1.0/KnowledgeTest/DeleteTest", { testId });
}

export function changeKnowledgeTestActive(testId: string, blocked: boolean): Promise<boolean> {
    return RestUtilities.post<boolean>("/api/v1.0/KnowledgeTest/ChangeActive", { testId, blocked });
}

export function getKnowledgeTestLog(testId: string, offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/KnowledgeTest/GetLog", { testId, offset, take });
}

export function getKnowledgeTopicSuggestions() {
    return new RestUtilWithCancel<KnowledgeTestTopicSuggestionDto[], { userId: string, searchText: string, offset: number, take: number; }>("get", "/api/v1.0/KnowledgeTest/GetTopicSuggestions");
}

export function getWhoAnsweredTest(testId: string, searchText: string, offset: number, take: number): Promise<PagedResult<KnowledgeTestWhoGotRightDto>> {
    return RestUtilities.get("/api/v1.0/KnowledgeTest/WhoAnsweredTest", { testId, searchText, offset, take });
}

export function getKnowledgeTestAnswersDistribution(testId: string): Promise<KnowledgeTestAnswersDistributionModel[]> {
    return RestUtilities.get("/api/v1.0/KnowledgeTest/GetKnowledgeTestAnswersDistribution", { testId });
}

export function getKnowledgeTestResultAnalysis(testId: string): Promise<KnowledgeTestResultAnalysisModel> {
    return RestUtilities.get("/api/v1.0/KnowledgeTest/GetKnowledgeTestResultAnalysisModel", { testId });
}

export function getWhoCanAnswerTest(topicId: string, searchText: string, offset: number, take: number): Promise<PagedResult<GenericListItemDto>> {
    return RestUtilities.get("/api/v1.0/KnowledgeTest/WhoCanAnswerTest", { topicId, searchText, offset, take });
}

export function getWhoCanAnswerTestSuggestions(topicId: string, searchText: string, offset: number, take: number): Promise<PagedResult<MemberSuggestionDto>> {
    return RestUtilities.get("/api/v1.0/KnowledgeTest/WhoCanAnswerTestSuggestions", { topicId, searchText, offset, take });
}
//#endregion

//#region GAMIFICATION

export function getBadgeTopicSuggestions() {
    return new RestUtilWithCancel<TopicSuggestionDto[], { userId: string, searchText: string, offset: number, take: number; }>("get", "/api/v1.0/Gamification/GetBadgeTopicSuggestions");
}

export function getAdminBadgeList(p: GetBadgeListParams): Promise<PagedResult<BadgeAdminListDto>> {
    return RestUtilities.post("/api/v1.0/Gamification/GetBadgeAdminList", p);
}

export function getBadgeRecord(badgeId: string): Promise<GetBadgeDto> {
    return RestUtilities.get("/api/v1.0/Gamification/GetBadgeRecord", { badgeId });
}

export function saveBadgeRecord(dto: SaveBadgeDto): Promise<void> {
    return RestUtilities.post("/api/v1.0/Gamification/SaveBadge", dto);
}

export function deleteBadge(badgeId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/Gamification/DeleteBadge", { badgeId });
}

export function checkBadgeNameIsUnique(badgeId: string, name: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/Gamification/CheckBadgeNameIsUnique", { badgeId, name });
}

export function getBadgeLog(badgeId: string, offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/Gamification/GetBadgeLog", { badgeId, offset, take });
}


export function getAwardRecord(): Promise<GetAwardDto> {
    return RestUtilities.get("/api/v1.0/Gamification/GetAwardRecord", {});
}

export function saveAwardRecord(dto: SaveAwardDto): Promise<void> {
    return RestUtilities.post("/api/v1.0/Gamification/SaveAward", dto);
}

export function getAwardLog(offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/Gamification/GetAwardLog", { offset, take });
}


export function getAwardTopicExceptions(p: PageRange): Promise<PagedResult<GetAwardTopicExceptionDto>> {
    return RestUtilities.get("/api/v1.0/Gamification/GetTopicExceptions", p);
}

export function saveAwardTopicException(dto: SaveAwardTopicExceptionDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/Gamification/SaveTopicException", dto);
}

export function deleteAwardTopicException(topicId: string): Promise<boolean> {
    return RestUtilities.delete<boolean>("/api/v1.0/Gamification/DeleteTopicException", { topicId });
}


//#endregion

//#region GLOBAL SEARCH

export function globalSearchPreview(searchText: string): Promise<GlobalSearchResult> {
    return RestUtilities.post<GlobalSearchResult>("/api/v1.0/GlobalSearch/SearchPreview", { searchText });
}
export function globalSearch(filter: GlobalPublicationSearchFilter, orderBy: GlobalSearchPublicationOrderBy, offset: number, take: number, othersTake: number): Promise<GlobalSearchResult> {
    return RestUtilities.post<GlobalSearchResult>("/api/v1.0/GlobalSearch/Search", { filter, orderBy, offset, take, othersTake });
}
export function globalSearchPublications(filter: GlobalPublicationSearchFilter, orderBy: GlobalSearchPublicationOrderBy, offset: number, take: number): Promise<GlobalSearchRecord> {
    return RestUtilities.post<GlobalSearchRecord>("/api/v1.0/GlobalSearch/SearchPublications", { filter, orderBy, offset, take });
}
export function globalSearchDocuments(filter: GlobalDocumentSearchFilter, orderBy: GlobalSearchDocumentOrderBy, offset: number, take: number): Promise<GlobalSearchRecord> {
    return RestUtilities.post<GlobalSearchRecord>("/api/v1.0/GlobalSearch/SearchDocuments", { filter, orderBy, offset, take });
}
export function globalSearchUsers(searchText: string, specialistOnly: boolean, offset: number, take: number): Promise<GlobalSearchRecord> {
    return RestUtilities.post<GlobalSearchRecord>("/api/v1.0/GlobalSearch/SearchUsers", { searchText, specialistOnly, offset, take });
}
export function globalSearchTopics(searchText: string, offset: number, take: number): Promise<GlobalSearchRecord> {
    return RestUtilities.post<GlobalSearchRecord>("/api/v1.0/GlobalSearch/SearchTopics", { searchText, offset, take });
}
export function globalSearchCourses(searchText: string, offset: number, take: number): Promise<GlobalSearchRecord> {
    return RestUtilities.post<GlobalSearchRecord>("/api/v1.0/GlobalSearch/SearchCourses", { searchText, offset, take });
}

//#endregion

//#region AZURE SEARCH

export function azureSearchImage(filter: ImageSearchFilter, offset: number, take: number): Promise<AzureSearchImageResult> {
    return RestUtilities.post("/api/v1.0/AzureSearch/ImageSearch", { filter, offset, take });
}

//#endregion

//#region NOTIFICATION

export function getCalloutNotification(): Promise<NotificationCalloutDto[]> {
    return RestUtilities.get("/api/v1.0/Notification/GetCallouts", null, true);
}
export function getNotificationCounter(): Promise<number> {
    return RestUtilities.post<number>("/api/v1.0/Notification/GetNotificationCounter", null, true);
}
export function getNotifications(): Promise<NotificationDto[]> {
    return RestUtilities.post<NotificationDto[]>("/api/v1.0/Notification/GetNotifications", {});
}
export function getNotificationOtherPeople(groupId: string): Promise<NotificationDto[]> {
    return RestUtilities.post<Array<any>>("/api/v1.0/Notification/GetNotificationOtherPeople", { groupId });
}
export function getNotificationOtherPublications(groupId: string): Promise<NotificationDto[]> {
    return RestUtilities.post<Array<any>>("/api/v1.0/Notification/GetNotificationOtherPublications", { groupId });
}

export function hideNotification(notificationId: string): Promise<boolean> {
    return RestUtilities.delete<boolean>("/api/v1.0/Notification/HideNotification", { notificationId });
}

export function hideAllNotifications(): Promise<boolean> {
    return RestUtilities.post<boolean>("/api/v1.0/Notification/HideAllUserNotifications", null);
}

export function updateUserEmailConfiguration(notificationType: NotificationType, emailOption: NotificationEmailOption): Promise<boolean> {
    return RestUtilities.post<boolean>("/api/v1.0/Notification/UpdateUserEmailConfiguration", { notificationType, emailOption });
}

export function updateUserNotificationConfiguration(dto: NotificationSetupItemDto): Promise<void> {
    return RestUtilities.post("/api/v1.0/Notification/SaveUserNotificationSetup", dto);
}

export function getUserNotificationConfig(): Promise<NotificationSetupDto> {
    return RestUtilities.post("/api/v1.0/Notification/GetUserNotificationSetup", null);
}

//#endregion

//#region LOCATION

export function getLocationSuggestions() {
    return new RestUtilWithCancel<AutoCompleteDataModel[], { searchText: string, offset: number, take: number; }>("post", "/api/v1.0/System/GetLocationSuggestions");
}

//#endregion

//#region SETUP

export function getAgreement(): Promise<GetAgreementDto> {
    return RestUtilities.get("/api/v1.0/System/GetAgreement", {});
}

export function getSetupAgreement(): Promise<GetSetupAgreementDto> {
    return RestUtilities.get("/api/v1.0/System/GetSetupAgreement", {});
}

export function saveSetupAgreement(dto: SaveSetupAgreementDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/System/SaveAgreement", dto);
}

export function getSetupAgreementLog(offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/System/GetAgreementLog", { offset, take });
}


export function getSetupAdmin(): Promise<GetSetupAdminDto> {
    return RestUtilities.get("/api/v1.0/System/GetSetupAdmin", {});
}

export function saveSetupAdmin(dto: SaveSetupAdminDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/System/SaveAdmin", dto);
}

export function getSetupAdminLog(offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/System/GetAdminLog", { offset, take });
}


export function getSetupIntegration(): Promise<GetSetupIntegrationDto> {
    return RestUtilities.get("/api/v1.0/System/GetSetupIntegration", {});
}

export function saveSetupIntegration(dto: SaveSetupIntegrationDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/System/SaveIntegration", dto);
}

export function getSetupIntegrationLog(offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/System/GetIntegrationLog", { offset, take });
}


export function getSetupUser(): Promise<GetSetupUserDto> {
    return RestUtilities.get("/api/v1.0/System/GetSetupUser", {});
}

export function saveSetupUser(dto: SaveSetupUserDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/System/SaveUser", dto);
}

export function getSetupUsersLog(offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/System/GetUserLog", { offset, take });
}


export function getSetupPublication(): Promise<GetSetupPublicationDto> {
    return RestUtilities.get("/api/v1.0/System/GetSetupPublication", {});
}

export function saveSetupPublication(dto: SaveSetupPublicationDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/System/SavePublication", dto);
}

export function getSetupPublicationLog(offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/System/GetPublicationLog", { offset, take });
}

export function getSetupKnowledgeTest(): Promise<GetSetupKnowledgeTestDto> {
    return RestUtilities.get("/api/v1.0/System/GetSetupKnowledgeTest", {});
}

export function saveSetupKnowledgeTest(dto: SaveSetupKnowledgeTestDto): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/System/SaveKnowledgeTest", dto);
}

export function getSetupKnowledgeTestLog(offset: number, take: number): Promise<PagedResult<LogRecord>> {
    return RestUtilities.get("/api/v1.0/System/GetKnowledgeTestLog", { offset, take });
}


export function getBlockCodesSelection(searchText: string, offset: number, take: number): Promise<PagedResult<GenericListItemDto>> {
    return RestUtilities.get("/api/v1.0/System/GetBlockCodesSelection", { searchText, offset, take });
}

export function getBlockCodes(p: PageRange): Promise<PagedResult<BlockCodeDto>> {
    return RestUtilities.get("/api/v1.0/System/GetBlockCodes", p);
}

export function saveBlockCode(blockCodeId: string, description: string): Promise<string> {
    return RestUtilities.post("/api/v1.0/System/SaveBlockCode", { blockCodeId, description });
}

export function deleteBlockCode(blockCodeId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/System/DeleteBlockCode", { blockCodeId });
}


export function getLocationsSelection(searchText: string, offset: number, take: number): Promise<PagedResult<GenericListItemDto>> {
    return RestUtilities.get("/api/v1.0/System/GetLocationsSelection", { searchText, offset, take });
}

export function getLocations(p: PageRange): Promise<PagedResult<LocationDto>> {
    return RestUtilities.get("/api/v1.0/System/GetLocations", p);
}

export function saveLocation(locationId: string, description: string): Promise<string> {
    return RestUtilities.post("/api/v1.0/System/SaveLocation", { locationId, description });
}

export function deleteLocation(locationId: string): Promise<boolean> {
    return RestUtilities.delete("/api/v1.0/System/DeleteLocation", { locationId });
}

//#endregion

//#region Storage

export function getMediaServiceFileList(p: GetMediaFileListParams): Promise<PagedResult<MediaServiceFileDto>> {
    return RestUtilities.post("/api/v1.0/Store/GetMediaServiceFileList", p);
}

export function reprocessMediaServiceFile(fileId: string): Promise<boolean> {
    return RestUtilities.post("/api/v1.0/Store/ReprocessMediaServiceFile", { fileId });
}

//#endregion

//#region QUEUE

export function getQueueEvents(p: QueueViewModelParams): Promise<PagedResult<QueueEventViewModel>> {
    return RestUtilities.post("/api/v1.0/System/GetQueueEvents", p);
}

//#endregion

//#region JOB LOG

export function getJobLogEventTypes(eventType: JobLogEventType): Promise<JobLogEventTypeDto[]> {
    return RestUtilities.get("/api/v1.0/System/GetJobLogEventTypes", { eventType });
}

export function getJobLogEvents(p: JobLogParams): Promise<PagedResult<JobLogDto>> {
    return RestUtilities.post("/api/v1.0/System/GetJobLogEvents", p);
}

//#endregion

//#region DASHBOARDS

/// USER DASHBOARD
export function getUserEvolutionPublicationResume(userId: string): Promise<DashboardUserEvolutionPublicationResumeModel> {
    return RestUtilities.get("/api/v1.0/Dashboard/GetEvolutionPublicationResume", { userId });
}
export function getUserEvolutionPublicationAnalytic(p: DashboardUserEvolutionPublicationParams): Promise<PagedResult<DashboardUserEvolutionPublicationListModel>> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetEvolutionPublicationAnalytic", p);
}

export function getUserEvolutionParticipationResume(userId: string): Promise<DashboardUserEvolutionParticipationResumeModel> {
    return RestUtilities.get("/api/v1.0/Dashboard/GetEvolutionParticipationResume", { userId });
}
export function getUserEvolutionParticipationAnalytic(p: DashboardUserEvolutionParticipationParams): Promise<PagedResult<DashboardUserEvolutionParticiationListModel>> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetEvolutionParticipationAnalytic", p);
}

export function getUserEvolutionKnowledgeResume(userId: string): Promise<DashboardKnowledgeIndicatorsResumeModel> {
    return RestUtilities.get("/api/v1.0/Dashboard/GetEvolutionKnowledgeResume", { userId });
}

export function getUserEvolutionKnowledgeAnalytic(p: DashboardUserEvolutionKnowdledgeParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetEvolutionKnowledgeAnalytic", p);
}

export function getUserEvolutionHasKnowledge(userId: string): Promise<boolean> {
    return RestUtilities.get("/api/v1.0/Dashboard/UserEvolutionHasKnowledge", { userId });
}


export function getUserAwardRankingList(userId: string, yearMonth: number, offset: number, take: number): Promise<AwardRankingListResult> {
    return RestUtilities.get("/api/v1.0/Dashboard/UserAwardRankingList", { userId, yearMonth, offset, take });
}

export function getUserAwardGraphStatement(userId: string): Promise<AwardHistoryGraphDto[]> {
    return RestUtilities.get("/api/v1.0/Dashboard/UserAwardGraphStatement", { userId });
}

export function getUserAwardStatement(userId: string, offset: number, take: number): Promise<PagedResult<AwardHistoryDto>> {
    return RestUtilities.get("/api/v1.0/Dashboard/UserAwardStatement", { userId, offset, take });
}


/// USERS AND GROUPS

export function getDashboardUserAndGroupsParticipationPublication(p: DashboardUserAndGroupsParticipationPublicationParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardUserAndGroupsParticipationPublication", p);
}

export function getDashboardUserAndGroupsParticipationAccess(p: DashboardUserAndGroupsParticipationAccessParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardUserAndGroupsParticipationAccess", p);
}

export function getDashboardUserAndGroupsParticipationKnowledgeTest(p: DashboardUserAndGroupsParticipationKnowledgeTestParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardUserAndGroupsParticipationKnowledgeTest", p);
}

export function getDashboardManagerTeamCounter(managerId: string, deepSearch: boolean): Promise<number> {
    return RestUtilities.get("/api/v1.0/Dashboard/GetManagerTeamCounter", { managerId, deepSearch });
}


/// PUBLICATION ANALYSIS

export function getDashboardPublicationAnalysis(p: DashboardPublicationAnalysisParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardPublicationAnalysis", p);
}


/// AWARD ANALYSIS

export function getDashboardAwardAnalysis(p: DashboardAwardAnalysisParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardAwardAnalysis", p);
}


/// COURSE ADMIN ANALYSIS

export function getDashboardCourseAdminAnalysis(courseId: string): Promise<DashboardCourseAdminAnalysisModel> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardCourseAdminAnalysis", { courseId });
}

/// COURSE ANALYSIS

export function getDashboardCourseAnalysis(p: DashboardCourseAnalysisParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardCourseAnalysis", p);
}

/// Knowledge Test

export function getDashboardKnowledgeTestApplied(p: DashboardKnowledgeTestAppliedParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardKnowledgeTestApplied", p);
}

export function getDashboardKnowledgeTestIndicators(p: DashboardKnowledgeTestIndicatorsParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardKnowledgeTestIndicators", p);
}

export function getDashboardKnowledgeTestAnalysis(p: DashboardKnowledgeTestAnalysisParams): Promise<any> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardKnowledgeTestAnalysis", p);
}


/// CONSUMPTION ANALYSIS

export function getDashboardConsumptionAnalysis(p: DashboardConsumptionAnalysisParams): Promise<DashboardConsumptionResultModel> {
    return RestUtilities.post("/api/v1.0/Dashboard/GetDashboardConsumptionAnalysis", p);
}

//#endregion


